/** Docs (UA): see modules/game/DOCS.md — synthesized SFX, no audio assets.
 *  Sound is OFF by default (public place); toggle lives on the start screen. */

export interface GameSound {
    setEnabled: (on: boolean) => void;
    pop: (streak: number) => void;
    ding: () => void;
    thud: () => void;
    threshold: () => void;
    finish: (won: boolean) => void;
}

export const createGameSound = (): GameSound => {
    let ctx: AudioContext | null = null;
    let enabled = false;

    const ensureCtx = (): AudioContext | null => {
        if (!enabled) return null;
        if (!ctx) {
            const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!Ctor) return null;
            ctx = new Ctor();
        }
        if (ctx.state === 'suspended') void ctx.resume();
        return ctx;
    };

    const tone = (
        freq: number,
        duration: number,
        type: OscillatorType,
        volume: number,
        delay = 0,
    ): void => {
        const audio = ensureCtx();
        if (!audio) return;
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        const t0 = audio.currentTime + delay;
        osc.type = type;
        osc.frequency.setValueAtTime(freq, t0);
        gain.gain.setValueAtTime(volume, t0);
        gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
        osc.connect(gain).connect(audio.destination);
        osc.start(t0);
        osc.stop(t0 + duration);
    };

    return {
        setEnabled: (on) => {
            enabled = on;
            if (on) ensureCtx();
        },
        // Pitch rises with the catch streak — the cheapest "juice" there is.
        pop: (streak) => tone(320 + Math.min(streak, 12) * 35, 0.09, 'triangle', 0.12),
        ding: () => {
            tone(880, 0.18, 'sine', 0.1);
            tone(1320, 0.24, 'sine', 0.06, 0.04);
        },
        thud: () => tone(90, 0.22, 'square', 0.14),
        threshold: () => {
            tone(523, 0.16, 'triangle', 0.1);
            tone(659, 0.16, 'triangle', 0.1, 0.08);
            tone(784, 0.28, 'triangle', 0.12, 0.16);
        },
        finish: (won) => {
            if (won) {
                tone(659, 0.14, 'triangle', 0.1);
                tone(784, 0.14, 'triangle', 0.1, 0.1);
                tone(1047, 0.4, 'triangle', 0.12, 0.2);
            } else {
                tone(392, 0.2, 'triangle', 0.08);
                tone(330, 0.32, 'triangle', 0.08, 0.12);
            }
        },
    };
};
